import React from 'react';
import { CalendarDays, TrendingUp, Percent, Clock, Coins, PiggyBank, AlertCircle, Award } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета пенсии
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  return (
    <ToolResult title="Результаты расчета пенсии">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Ежемесячная пенсия */}
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <Coins className="w-4 h-4"/>
            Ежемесячная пенсия
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-emerald-800 dark:text-emerald-200" aria-live="polite">
              {formatCurrency(results.monthlyPension)}
            </span>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            В возрасте {results.retirementAge} лет
          </p>
        </div>

        {/* Коэффициент замещения */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <Percent className="w-4 h-4"/>
            Коэффициент замещения
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {formatNumber(results.replacementRate, 1)}%
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            Процент от вашей последней зарплаты
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Возраст выхода на пенсию" 
          value={`${results.retirementAge} лет (через ${results.yearsToRetirement} ${formatYearsLabel(results.yearsToRetirement)})`}
          color="emerald" 
          icon={<Clock className="w-4 h-4"/>}
        />
        
        {results.earlyRetirementYears > 0 && (
          <ResultItem 
            label="Досрочный выход на пенсию" 
            value={`На ${results.earlyRetirementYears} ${formatYearsLabel(results.earlyRetirementYears)} раньше обычного`} 
            color="green" 
            icon={<Award className="w-4 h-4"/>}
          />
        )}

        <ResultItem 
          label="Страховая пенсия" 
          value={formatCurrency(results.insurancePension)} 
          color="blue" 
          icon={<Coins className="w-4 h-4"/>}
        />
        
        {results.voluntaryPension > 0 && (
          <ResultItem 
            label="Накопительная пенсия" 
            value={formatCurrency(results.voluntaryPension)} 
            color="indigo" 
            icon={<PiggyBank className="w-4 h-4"/>} 
          />
        )}
        
        {results.regionalBonus > 0 && (
          <ResultItem 
            label="Региональная доплата" 
            value={formatCurrency(results.regionalBonus)} 
            color="purple" 
          />
        )}
        
        {results.ruralBonus > 0 && (
          <ResultItem 
            label="Сельская надбавка" 
            value={formatCurrency(results.ruralBonus)} 
            color="emerald" 
          />
        )}
        
        <ResultItem 
          label="Общий стаж" 
          value={`${formatNumber(results.totalExperience, 1)} лет`} 
          icon={<CalendarDays className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Пенсионные баллы (ИПК)" 
          value={`${formatNumber(results.totalPensionPoints, 2)} баллов`}
          color="orange" 
        />
        
        {results.professionalCoefficientBonus > 1 && (
          <ResultItem 
            label="Профессиональный коэффициент" 
            value={`${formatNumber(results.professionalCoefficientBonus, 2)}`} 
            color="purple" 
          />
        )}
        
        <ResultItem 
          label="Накопления к моменту выхода на пенсию" 
          value={formatCurrency(results.totalSavingsAtRetirement)} 
          color="teal" 
          icon={<PiggyBank className="w-4 h-4"/>} 
        />

        {/* Информация о результатах */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">О расчете пенсии</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Расчет учитывает текущее законодательство РФ, включая стоимость пенсионного коэффициента и фиксированную выплату, актуальные на {new Date().getFullYear()} год. 
                Прогноз основан на указанных вами параметрах и может отличаться от фактической пенсии в будущем.
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова "год" в зависимости от числа
const formatYearsLabel = (years) => {
  const lastDigit = years % 10;
  const lastTwoDigits = years % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'лет';
  if (lastDigit === 1) return 'год';
  if (lastDigit >= 2 && lastDigit <= 4) return 'года';
  return 'лет';
};

export default ResultsOverview;